// Componente principal del tab Teams.
// Detecta el estado del usuario y renderiza la vista correcta:
//   - Sin plan Teams → pricing UI con tiers y redirect a Whop
//   - Con plan Teams pero sin team → formulario de creación
//   - Owner → OwnerDashboardView
//   - Player → PlayerTeamView (puede estar en múltiples teams)

import React, { useState, useEffect } from 'react';
import { useT } from '../lib/i18n';
import { User } from '@supabase/supabase-js';
import { useTeam } from '../hooks/useTeam';
import { supabase, SUPABASE_ENABLED } from '../lib/supabase';
import { PlayerTeamView } from './teams/PlayerTeamView';
import { OwnerDashboardView } from './teams/OwnerDashboardView';
import { TeamConsentScreen } from './teams/TeamConsentScreen';

// Tiers de pricing (precios en USD, por mes o por año total)
const TIERS = [
  { seats: 5,  label: 'STARTER', planMonthly: 'teams-5-monthly',  planAnnual: 'teams-5-annual',  monthlyPrice: 25,  annualPrice: 240  },
  { seats: 10, label: 'GROWTH',  planMonthly: 'teams-10-monthly', planAnnual: 'teams-10-annual', monthlyPrice: 50,  annualPrice: 480  },
  { seats: 20, label: 'PRO',     planMonthly: 'teams-20-monthly', planAnnual: 'teams-20-annual', monthlyPrice: 100, annualPrice: 960  },
];

interface Props {
  user: User | null;
  plan: 'free' | 'pro';
  teamSeats: number | null;
  onUpgrade: () => void;
}

export function TeamsView({ user, plan, teamSeats, onUpgrade }: Props) {
  const { t } = useT();
  const { loading, teams, toggleSync, leaveTeam, revokeInvite, transferOwnership, loadDashboardData, refresh } = useTeam(user);

  // Invitación pendiente detectada al abrir el tab (usuario ya estaba logueado)
  const [pendingToken, setPendingToken] = useState<string | null>(null);

  useEffect(() => {
    if (!user || !SUPABASE_ENABLED) return;
    let cancelled = false;

    (async () => {
      try {
        const { data: sessionData } = await supabase.auth.getSession();
        const jwt = sessionData?.session?.access_token;
        if (!jwt || cancelled) return;

        const apiBase = process.env.REACT_APP_API_URL || '';
        const res = await fetch(`${apiBase}/api/teams/pending-invite`, {
          headers: { 'Authorization': `Bearer ${jwt}` },
        });
        if (!res.ok || cancelled) return;

        const data = await res.json();
        if (data.token && !cancelled) setPendingToken(data.token);
      } catch {
        // silencioso — no bloquear el tab si el check falla
      }
    })();

    return () => { cancelled = true; };
  }, [user]);

  // Crear team: estado del formulario
  const [showCreate,  setShowCreate]  = useState(false);
  const [createName,  setCreateName]  = useState('');
  const [creating,    setCreating]    = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  // Pricing: estado del selector de tiers
  const [selectedTierIdx, setSelectedTierIdx] = useState(0);
  const [billing,         setBilling]         = useState<'monthly' | 'annual'>('annual');
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError,   setCheckoutError]   = useState<string | null>(null);

  // ── Helpers de slug ───────────────────────────────────────────────────────────

  const nameToSlug = (name: string) =>
    name.toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .slice(0, 40);

  // ── Checkout hacia Whop ───────────────────────────────────────────────────────

  const handleCheckout = async () => {
    if (!user || !SUPABASE_ENABLED) return;
    setCheckoutLoading(true);
    setCheckoutError(null);

    try {
      const { data: session } = await supabase.auth.getSession();
      const jwt = session?.session?.access_token;
      if (!jwt) throw new Error('No hay sesión activa');

      const tier = TIERS[selectedTierIdx];
      const planType = billing === 'annual' ? tier.planAnnual : tier.planMonthly;
      const apiBase = process.env.REACT_APP_API_URL || '';

      const res = await fetch(`${apiBase}/api/whop/create-checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${jwt}` },
        body: JSON.stringify({ plan: planType }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || t('upgrade.errorCheckout'));
      window.location.href = data.url;
    } catch (err: any) {
      setCheckoutError(err.message);
      setCheckoutLoading(false);
    }
  };

  // ── Crear team ───────────────────────────────────────────────────────────────

  const handleCreateTeam = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!SUPABASE_ENABLED || !user || !teamSeats) return;

    setCreating(true);
    setCreateError(null);

    // RPC transaccional: crea team + membership en una sola transacción
    // Evita que un fallo en el segundo INSERT deje un team sin owner
    try {
      const { data: teamId, error: rpcErr } = await supabase.rpc('create_team_with_owner', {
        p_name:        createName.trim(),
        p_slug:        nameToSlug(createName.trim()),
        p_seats_total: teamSeats,
      });

      if (rpcErr || !teamId) throw new Error(t('teams.createError'));

      setShowCreate(false);
      setCreateName('');
      refresh();
    } catch (err: any) {
      setCreateError(err.message ?? t('teams.createError'));
    } finally {
      setCreating(false);
    }
  };

  // ── Estilos ──────────────────────────────────────────────────────────────────

  const card: React.CSSProperties = {
    background: '#111520',
    border: '1px solid #1e2636',
    borderRadius: '8px',
    padding: '1.5rem',
    marginBottom: '1rem',
    fontFamily: 'var(--mono)',
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    background: '#151b2a',
    border: '1px solid #1e2636',
    borderRadius: '4px',
    padding: '0.5rem 0.75rem',
    color: 'var(--text)',
    fontFamily: 'var(--mono)',
    fontSize: '0.85rem',
    boxSizing: 'border-box',
  };

  const formLabel: React.CSSProperties = {
    fontSize: '0.7rem',
    color: 'var(--muted)',
    letterSpacing: '1.5px',
    textTransform: 'uppercase',
    marginBottom: '0.35rem',
    display: 'block',
  };

  // ── Render ────────────────────────────────────────────────────────────────────

  // Overlay de consent: se muestra cuando hay invitación pendiente detectada al abrir el tab
  const consentOverlay = pendingToken ? (
    <TeamConsentScreen
      token={pendingToken}
      user={user}
      onDone={(joined) => {
        setPendingToken(null);
        if (joined) refresh();
      }}
    />
  ) : null;

  if (loading) {
    return (
      <>
        {consentOverlay}
        <div style={{ textAlign: 'center', color: 'var(--muted)', padding: '3rem', fontFamily: 'var(--mono)' }}>
          {t('teams.loading')}
        </div>
      </>
    );
  }

  // Sin team: mostrar pricing si no tiene plan, o form de creación si ya pagó
  if (teams.length === 0) {
    // ── Sin plan Teams: pricing UI ──────────────────────────────────────────
    if (!teamSeats) {
      const tier = TIERS[selectedTierIdx];
      const price = billing === 'annual' ? tier.annualPrice : tier.monthlyPrice;
      const priceLabel = billing === 'annual'
        ? `$${price}/${t('teams.pricingPerYear')}`
        : `$${price}/${t('teams.pricingPerMonth')}`;

      return (
        <>
          {consentOverlay}
          <div style={{ maxWidth: '680px', margin: '0 auto', padding: '1.5rem 1rem' }}>
          <div style={card}>
            {/* Header */}
            <div style={{ marginBottom: '1.5rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                <span style={{ background: '#4a9ae022', border: '1px solid #4a9ae055', color: '#4a9ae0', fontSize: '0.7rem', fontWeight: 700, letterSpacing: '2px', padding: '3px 10px', borderRadius: '3px' }}>TEAMS ◈</span>
                <span style={{ fontSize: '0.7rem', color: '#4a9ae0', letterSpacing: '1px' }}>{t('teams.pricingLaunchBadge')}</span>
              </div>
              <h2 style={{ margin: '0 0 0.25rem', fontSize: '1.1rem', color: 'var(--text)', letterSpacing: '1px' }}>{t('teams.pricingTitle')}</h2>
              <p style={{ margin: 0, fontSize: '0.82rem', color: 'var(--muted)' }}>{t('teams.pricingSubtitle')}</p>
            </div>

            {/* Billing toggle */}
            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.25rem' }}>
              {(['monthly', 'annual'] as const).map(b => (
                <button
                  key={b}
                  onClick={() => setBilling(b)}
                  style={{
                    padding: '0.35rem 0.85rem', borderRadius: '4px', cursor: 'pointer', fontSize: '0.78rem', fontFamily: 'var(--display)', fontWeight: 700, letterSpacing: '0.5px',
                    background: billing === b ? '#4a9ae0' : 'transparent',
                    color: billing === b ? '#fff' : 'var(--muted)',
                    border: billing === b ? 'none' : '1px solid #1e2636',
                  }}
                >
                  {b === 'monthly' ? t('teams.pricingMonthly') : t('teams.pricingAnnual')}
                </button>
              ))}
            </div>

            {/* Tier cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.75rem', marginBottom: '1.5rem' }}>
              {TIERS.map((tier, i) => {
                const tierPrice = billing === 'annual' ? tier.annualPrice : tier.monthlyPrice;
                const perSeat   = billing === 'annual' ? 48 : 5;
                const isSelected = selectedTierIdx === i;
                return (
                  <button
                    key={tier.seats}
                    onClick={() => setSelectedTierIdx(i)}
                    style={{
                      background: isSelected ? '#4a9ae015' : '#0d1117',
                      border: `1px solid ${isSelected ? '#4a9ae0' : '#1e2636'}`,
                      borderRadius: '6px', padding: '1rem 0.75rem', cursor: 'pointer', textAlign: 'left', fontFamily: 'var(--mono)',
                    }}
                  >
                    <div style={{ fontSize: '0.65rem', color: isSelected ? '#4a9ae0' : 'var(--muted)', letterSpacing: '2px', marginBottom: '0.4rem' }}>{tier.label}</div>
                    <div style={{ fontSize: '0.85rem', color: 'var(--text)', fontWeight: 700 }}>{tier.seats} seats</div>
                    <div style={{ fontSize: '1.1rem', color: isSelected ? '#4a9ae0' : 'var(--text)', fontWeight: 700, margin: '0.35rem 0 0.2rem' }}>${tierPrice}</div>
                    <div style={{ fontSize: '0.65rem', color: 'var(--muted)' }}>${perSeat}/seat/{billing === 'annual' ? t('teams.pricingPerYear') : t('teams.pricingPerMonth')}</div>
                  </button>
                );
              })}
            </div>

            {/* Features */}
            <div style={{ marginBottom: '1.5rem', display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
              {([1,2,3,4] as const).map(n => (
                <div key={n} style={{ display: 'flex', gap: '0.5rem', fontSize: '0.8rem', color: 'var(--muted)' }}>
                  <span style={{ color: '#52c2a0', flexShrink: 0 }}>✓</span>
                  <span>{t(`teams.pricingFeature${n}` as any)}</span>
                </div>
              ))}
            </div>

            {/* CTA */}
            {checkoutError && <p style={{ color: '#e05252', fontSize: '0.78rem', margin: '0 0 0.75rem' }}>{checkoutError}</p>}
            <button className="btn btn-primary" onClick={handleCheckout} disabled={checkoutLoading} style={{ width: '100%', justifyContent: 'center', marginBottom: '0.75rem' }}>
              {checkoutLoading ? '...' : `${t('teams.pricingCheckoutBtn')} ${priceLabel}`}
            </button>
            <p style={{ margin: 0, fontSize: '0.72rem', color: 'var(--muted)', textAlign: 'center' }}>
              {t('teams.pricingContact')}{' '}
              <a href="mailto:support@grindlog.app?subject=Teams%20plan" style={{ color: '#4a9ae0', textDecoration: 'none' }}>support@grindlog.app</a>
            </p>
          </div>
          </div>
        </>
      );
    }

    // ── Tiene plan Teams pero no creó team todavía ─────────────────────────
    return (
      <>
        {consentOverlay}
        <div style={{ maxWidth: '560px', margin: '0 auto', padding: '1.5rem 1rem' }}>
        <div style={card}>
          <div style={{ marginBottom: '1rem' }}>
            <p style={{ margin: '0 0 0.25rem', fontSize: '0.82rem', color: '#52c2a0' }}>
              {t('teams.seatsPlanActive', { seats: String(teamSeats) })}
            </p>
            <p style={{ margin: 0, fontSize: '0.78rem', color: 'var(--muted)' }}>{t('teams.noTeamSub')}</p>
          </div>

          {!showCreate ? (
            <button className="btn btn-primary" onClick={() => setShowCreate(true)}>
              {t('teams.createBtn')}
            </button>
          ) : (
            <form onSubmit={handleCreateTeam}>
              <div style={{ marginBottom: '1.25rem' }}>
                <label style={formLabel}>{t('teams.createName')}</label>
                <input style={inputStyle} value={createName} onChange={e => setCreateName(e.target.value)} placeholder={t('teams.createNamePlaceholder')} required />
              </div>
              <div style={{ marginBottom: '1rem', fontSize: '0.78rem', color: 'var(--muted)' }}>
                {t('teams.createSeats')}: <strong style={{ color: 'var(--text)' }}>{teamSeats}</strong>
              </div>
              {createError && <p style={{ color: '#e05252', fontSize: '0.78rem', margin: '0 0 0.75rem' }}>{createError}</p>}
              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <button className="btn btn-primary" type="submit" disabled={creating}>{creating ? t('teams.creating') : t('teams.createBtn')}</button>
                <button className="btn btn-secondary" type="button" onClick={() => { setShowCreate(false); setCreateError(null); }}>Cancelar</button>
              </div>
            </form>
          )}
        </div>
        </div>
      </>
    );
  }

  // Con teams: renderizar cada team según el rol
  return (
    <>
      {consentOverlay}
      <div style={{ padding: '1.5rem 1rem' }}>
      {teams.map(ctx => (
        <div key={ctx.team.id} style={{ marginBottom: '2rem' }}>
          {ctx.membership.role === 'owner' ? (
            <OwnerDashboardView
              ctx={ctx}
              loadDashboardData={loadDashboardData}
              revokeInvite={revokeInvite}
              transferOwnership={transferOwnership}
            />
          ) : (
            <PlayerTeamView
              ctx={ctx}
              onToggleSync={(enabled) => toggleSync(ctx.team.id, enabled)}
              onLeave={(anonymize) => leaveTeam(ctx.team.id, anonymize)}
            />
          )}
        </div>
      ))}
      </div>
    </>
  );
}
